import { Link } from "react-router-dom";
import type { Comic } from "@/types/comic";
import StatusBadge from "./StatusBadge";
import { formatViews } from "@/utils/comics";

interface ComicCardProps {
  comic: Comic;
  variant?: "grid" | "row";
}

export function ComicGridCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/detail/${comic.id}`}
      className="group flex flex-col overflow-hidden rounded-lg bg-background-50 transition-transform duration-200 hover:-translate-y-1"
    >
      <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
        <div className="aspect-[3/4] w-full">
          <img
            src={comic.cover}
            alt={`${comic.title} 韩漫封面`}
            title={`${comic.title} 免费韩漫在线阅读`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="absolute left-2 top-2">
          <StatusBadge comic={comic} />
        </div>
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] text-white">
          <i className="ri-eye-line" />
          {formatViews(comic.views)}
        </div>
      </div>
      <div className="flex flex-1 flex-col pt-2.5">
        <h3 className="line-clamp-1 text-sm font-semibold text-foreground-950">
          {comic.title}
        </h3>
        <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
          {comic.author} · {comic.categories.join(" / ")}
        </p>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
          {comic.summary}
        </p>
        <div className="mt-2 flex items-center justify-between border-t border-background-200 pt-2 text-[11px] text-foreground-500">
          <span className="line-clamp-1 text-primary-700">{comic.latestChapter}</span>
          <span className="hidden shrink-0 items-center gap-0.5 text-foreground-400 sm:flex">
            <i className="ri-time-line" />
            {comic.updatedAt.slice(5)}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ComicRowCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/detail/${comic.id}`}
      className="group flex items-start gap-3 rounded-lg bg-background-50 p-2.5 transition-colors duration-200 hover:bg-primary-50"
    >
      <div className="h-[104px] w-[78px] shrink-0 overflow-hidden rounded-md bg-background-200">
        <img
          src={comic.cover}
          alt={`${comic.title} 韩漫封面`}
          title={`${comic.title} 免费韩漫在线阅读`}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <h3 className="line-clamp-1 text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
            {comic.title}
          </h3>
          <StatusBadge comic={comic} />
        </div>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
          {comic.summary}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {comic.author}
          </span>
          <span className="flex items-center gap-1 text-primary-700">
            <i className="ri-book-read-line" />
            {comic.latestChapter}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {comic.updatedAt}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ComicCard({ comic, variant = "grid" }: ComicCardProps) {
  if (variant === "row") return <ComicRowCard comic={comic} />;
  return <ComicGridCard comic={comic} />;
}